import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Users, Crown, CheckCircle, XCircle, Shield, BarChart3,
  FileText, CreditCard, Eye, Copy, LayoutGrid, Plus, Trash2, BookmarkPlus, Home,
  Search, Bell, Settings, TrendingUp, ArrowUpRight, Filter, Loader2,
} from "lucide-react";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

const planColors = {
  free: { bg: "bg-gray-100", text: "text-gray-600", label: "Free" },
  pro: { bg: "bg-blue-100", text: "text-blue-700", label: "Pro" },
  enterprise: { bg: "bg-amber-100", text: "text-amber-700", label: "Entreprise" },
};

const sidebarItems = [
  { id: "dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "users", label: "Utilisateurs", icon: Users },
  { id: "licenses", label: "Licences", icon: Crown },
  { id: "payments", label: "Paiements", icon: CreditCard },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "templates", label: "Templates", icon: LayoutGrid },
  { id: "settings", label: "Reglages", icon: Settings },
];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeSection, setActiveSection] = useState("dashboard");

  // Filters
  const [userSearch, setUserSearch] = useState("");
  const [userPlanFilter, setUserPlanFilter] = useState("all");
  const [licenseSearch, setLicenseSearch] = useState("");

  // Template creation
  const [newTplTitle, setNewTplTitle] = useState("");
  const [newTplDesc, setNewTplDesc] = useState("");
  const [newTplCategory, setNewTplCategory] = useState("custom");

  // Settings
  const [maxFreeSchemas, setMaxFreeSchemas] = useState(3);
  const [maxProSchemas, setMaxProSchemas] = useState(20);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [stripePublicKey, setStripePublicKey] = useState("");
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpEmail, setSmtpEmail] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpFromName, setSmtpFromName] = useState("CardFlow");
  const [testingSmtp, setTestingSmtp] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") { navigate("/dashboard"); return; }
    loadData();
  }, [user, navigate]);

  const loadData = useCallback(async () => {
    try {
      const [usersRes, statsRes, paymentsRes, licensesRes, reviewsRes, templatesRes, notifsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/payments`, { withCredentials: true }),
        axios.get(`${API}/admin/licenses`, { withCredentials: true }),
        axios.get(`${API}/admin/reviews`, { withCredentials: true }),
        axios.get(`${API}/templates`, { withCredentials: true }),
        axios.get(`${API}/admin/notifications`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setPayments(paymentsRes.data);
      setLicenses(licensesRes.data);
      setReviews(reviewsRes.data);
      setTemplates(templatesRes.data);
      setNotifications(notifsRes.data);
      if (settingsRes.data) {
        if (settingsRes.data.free_diagram_limit !== undefined) setMaxFreeSchemas(settingsRes.data.free_diagram_limit);
        if (settingsRes.data.pro_diagram_limit !== undefined) setMaxProSchemas(settingsRes.data.pro_diagram_limit);
        if (settingsRes.data.stripe_public_key) setStripePublicKey(settingsRes.data.stripe_public_key);
        if (settingsRes.data.stripe_secret_key) setStripeSecretKey(settingsRes.data.stripe_secret_key);
        if (settingsRes.data.smtp_host) setSmtpHost(settingsRes.data.smtp_host);
        if (settingsRes.data.smtp_port) setSmtpPort(settingsRes.data.smtp_port);
        if (settingsRes.data.smtp_email) setSmtpEmail(settingsRes.data.smtp_email);
        if (settingsRes.data.smtp_password) setSmtpPassword(settingsRes.data.smtp_password);
        if (settingsRes.data.smtp_from_name) setSmtpFromName(settingsRes.data.smtp_from_name);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const handleAction = async (userId, action) => {
    setActionLoading(`${userId}-${action}`);
    try {
      const res = await axios.post(`${API}/admin/users/action`, { user_id: userId, action }, { withCredentials: true });
      toast.success(res.data.message);
      await loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setActionLoading(null); }
  };

  const createTemplate = async () => {
    if (!newTplTitle.trim()) return;
    try {
      await axios.post(`${API}/templates`, { title: newTplTitle, description: newTplDesc, category: newTplCategory, nodes: [], edges: [] }, { withCredentials: true });
      setNewTplTitle(""); setNewTplDesc("");
      toast.success("Template cree");
      await loadData();
    } catch (e) { toast.error("Erreur"); }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/templates/${id}`, { withCredentials: true });
      toast.success("Template supprimé");
      await loadData();
    } catch (e) { toast.error("Erreur"); }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        free_diagram_limit: maxFreeSchemas,
        pro_diagram_limit: maxProSchemas,
        stripe_public_key: stripePublicKey,
        stripe_secret_key: stripeSecretKey,
        smtp_host: smtpHost,
        smtp_port: smtpPort,
        smtp_email: smtpEmail,
        smtp_password: smtpPassword,
        smtp_from_name: smtpFromName,
      }, { withCredentials: true });
      toast.success("Reglages sauvegardes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setSettingsSaving(false);
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const { data } = await axios.post(`${API}/admin/test-smtp`, { email: smtpEmail }, { withCredentials: true });
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Echec du test SMTP");
    }
    setTestingSmtp(false);
  };

  // Filtered users
  const filteredUsers = users.filter(u => {
    const matchSearch = !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase());
    const matchPlan = userPlanFilter === "all" || u.plan === userPlanFilter || (userPlanFilter === "free" && !u.plan);
    return matchSearch && matchPlan;
  });

  // Filtered licenses
  const filteredLicenses = licenses.filter(l =>
    !licenseSearch || l.name?.toLowerCase().includes(licenseSearch.toLowerCase()) || l.email?.toLowerCase().includes(licenseSearch.toLowerCase())
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50/50"><p className="text-sm text-gray-400 animate-pulse">Chargement...</p></div>;

  const planBreakdown = {
    free: users.filter(u => !u.plan || u.plan === "free").length,
    pro: users.filter(u => u.plan === "pro").length,
    enterprise: users.filter(u => u.plan === "enterprise").length,
  };
  const totalRevenue = payments.filter(p => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50/50 flex" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-gray-200 h-screen sticky top-0 flex flex-col shrink-0" data-testid="admin-sidebar">
        <div className="px-5 h-14 flex items-center gap-2 border-b border-gray-100">
          <Shield size={18} className="text-indigo-600" />
          <span className="font-heading text-sm font-bold text-gray-900">Administration</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {sidebarItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${
                  activeSection === item.id ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                }`}
                data-testid={`admin-nav-${item.id}`}
              >
                <Icon size={15} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-gray-100">
          <button onClick={() => navigate("/dashboard")} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors" data-testid="admin-back-btn">
            <Home size={15} /> Retour au dashboard
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-h-screen">
        <header className="h-14 bg-white/80 backdrop-blur-xl border-b border-gray-200 flex items-center px-8 sticky top-0 z-40">
          <h1 className="font-heading text-base font-bold text-gray-900 capitalize">{sidebarItems.find(s => s.id === activeSection)?.label || "Admin"}</h1>
        </header>

        <div className="p-8">
          {/* ─── DASHBOARD ─── */}
          {activeSection === "dashboard" && stats && (
            <div>
              {/* Stats row 1 */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                {[
                  { label: "Utilisateurs", value: stats.total_users, icon: Users, color: "#6366F1" },
                  { label: "Diagrammes", value: stats.total_diagrams, icon: FileText, color: "#3B82F6" },
                  { label: "Paiements", value: stats.total_payments, icon: CreditCard, color: "#7C3AED" },
                  { label: "Revenue totale", value: `${totalRevenue} EUR`, icon: TrendingUp, color: "#10B981" },
                ].map(stat => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: stat.color + "12" }}>
                        <Icon size={16} style={{ color: stat.color }} />
                      </div>
                      <p className="text-2xl font-bold text-gray-900 font-mono">{stat.value}</p>
                      <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mt-1">{stat.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Plan breakdown */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm mb-6">
                <h3 className="font-semibold text-sm text-gray-900 mb-4 flex items-center gap-2"><Crown size={14} className="text-amber-500" /> Répartition des plans</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { plan: "free", count: planBreakdown.free, pct: stats.total_users ? Math.round(planBreakdown.free / stats.total_users * 100) : 0, color: "#9CA3AF" },
                    { plan: "pro", count: planBreakdown.pro, pct: stats.total_users ? Math.round(planBreakdown.pro / stats.total_users * 100) : 0, color: "#3B82F6" },
                    { plan: "enterprise", count: planBreakdown.enterprise, pct: stats.total_users ? Math.round(planBreakdown.enterprise / stats.total_users * 100) : 0, color: "#F59E0B" },
                  ].map(p => (
                    <div key={p.plan} className="flex items-center gap-3" data-testid={`plan-stat-${p.plan}`}>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white" style={{ backgroundColor: p.color }}>
                        {p.count}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{planColors[p.plan]?.label}</p>
                        <p className="text-[10px] text-gray-400">{p.pct}% des utilisateurs</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent activity */}
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h3 className="font-semibold text-sm text-gray-900 mb-3 flex items-center justify-between">
                    Derniers utilisateurs
                    <button onClick={() => setActiveSection("users")} className="text-[10px] text-indigo-600 font-semibold flex items-center gap-0.5 hover:underline"><ArrowUpRight size={10} /> Voir tout</button>
                  </h3>
                  <div className="space-y-2">
                    {users.slice(0, 5).map(u => (
                      <div key={u.id} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                        <div>
                          <p className="text-xs font-semibold text-gray-900">{u.name}</p>
                          <p className="text-[10px] text-gray-400">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${planColors[u.plan || "free"]?.bg} ${planColors[u.plan || "free"]?.text}`}>
                            {planColors[u.plan || "free"]?.label}
                          </span>
                          {u.is_validated ? <CheckCircle size={11} className="text-emerald-500" /> : <XCircle size={11} className="text-gray-300" />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h3 className="font-semibold text-sm text-gray-900 mb-3 flex items-center justify-between">
                    Derniers paiements
                    <button onClick={() => setActiveSection("payments")} className="text-[10px] text-indigo-600 font-semibold flex items-center gap-0.5 hover:underline"><ArrowUpRight size={10} /> Voir tout</button>
                  </h3>
                  {payments.length === 0 ? <p className="text-xs text-gray-400">Aucun paiement</p> : (
                    <div className="space-y-2">
                      {payments.slice(0, 5).map((p, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                          <p className="text-xs text-gray-700">{p.email}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono font-bold text-gray-900">{p.amount} EUR</span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                              {p.payment_status === "paid" ? "Paye" : "En cours"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ─── USERS ─── */}
          {activeSection === "users" && (
            <div>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Rechercher par nom ou email..."
                    className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                    data-testid="user-search"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <Filter size={12} className="text-gray-400" />
                  {["all", "free", "pro", "enterprise"].map(f => (
                    <button
                      key={f}
                      onClick={() => setUserPlanFilter(f)}
                      className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${
                        userPlanFilter === f ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                      }`}
                      data-testid={`filter-${f}`}
                    >
                      {f === "all" ? "Tous" : planColors[f]?.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="users-table">
                    <thead>
                      <tr className="border-b border-gray-100 text-left">
                        {["Utilisateur", "Role", "Statut", "Plan", "Schemas", "Inscription", "Actions"].map(h => (
                          <th key={h} className="px-5 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(u => {
                        const plan = u.plan || "free";
                        return (
                          <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`user-row-${u.id}`}>
                            <td className="px-5 py-3"><p className="font-semibold text-sm text-gray-900">{u.name}</p><p className="text-[10px] text-gray-400">{u.email}</p></td>
                            <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600"}`}>{u.role === "admin" ? "Admin" : "User"}</span></td>
                            <td className="px-5 py-3">{u.is_validated ? <span className="flex items-center gap-1 text-xs text-emerald-600"><CheckCircle size={12} /> Validé</span> : <span className="flex items-center gap-1 text-xs text-amber-600"><XCircle size={12} /> Attente</span>}</td>
                            <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${planColors[plan]?.bg} ${planColors[plan]?.text}`}>{planColors[plan]?.label}</span></td>
                            <td className="px-5 py-3 font-mono text-sm text-gray-700">{u.diagram_count}</td>
                            <td className="px-5 py-3 text-xs text-gray-400">{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                            <td className="px-5 py-3">{u.role !== "admin" && (
                              <div className="flex gap-1.5 flex-wrap">
                                {!u.is_validated ? (
                                  <button onClick={() => handleAction(u.id, "validate")} disabled={actionLoading === `${u.id}-validate`} className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-emerald-100 disabled:opacity-50" data-testid={`validate-${u.id}`}>Validér</button>
                                ) : (
                                  <button onClick={() => handleAction(u.id, "invalidate")} className="bg-gray-100 text-gray-600 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-gray-200" data-testid={`invalidate-${u.id}`}>Désactiver</button>
                                )}
                                {plan === "free" && (
                                  <>
                                    <button onClick={() => handleAction(u.id, "grant_pro")} className="bg-blue-50 text-blue-700 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-blue-100" data-testid={`grant-pro-${u.id}`}>Pro</button>
                                    <button onClick={() => handleAction(u.id, "grant_premium")} className="bg-amber-50 text-amber-700 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-amber-100" data-testid={`grant-enterprise-${u.id}`}>Entreprise</button>
                                  </>
                                )}
                                {plan === "pro" && (
                                  <>
                                    <button onClick={() => handleAction(u.id, "grant_premium")} className="bg-amber-50 text-amber-700 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-amber-100" data-testid={`grant-enterprise-${u.id}`}>Entreprise</button>
                                    <button onClick={() => handleAction(u.id, "revoke_premium")} className="bg-rose-50 text-rose-700 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-rose-100" data-testid={`revoke-${u.id}`}>Revoquer</button>
                                  </>
                                )}
                                {plan === "enterprise" && (
                                  <button onClick={() => handleAction(u.id, "revoke_premium")} className="bg-rose-50 text-rose-700 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-rose-100" data-testid={`revoke-${u.id}`}>Revoquer</button>
                                )}
                              </div>
                            )}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="px-5 py-3 border-t border-gray-100 text-[10px] text-gray-400">
                  {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? "s" : ""} {userSearch || userPlanFilter !== "all" ? "(filtre)" : ""}
                </div>
              </div>
            </div>
          )}

          {/* ─── LICENSES ─── */}
          {activeSection === "licenses" && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input
                    value={licenseSearch}
                    onChange={e => setLicenseSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                    data-testid="license-search"
                  />
                </div>
              </div>

              {/* Plan summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="license-free-count">
                  <p className="text-[10px] text-gray-400 font-semibold uppercase">Free</p>
                  <p className="text-2xl font-bold text-gray-900 font-mono">{planBreakdown.free}</p>
                </div>
                <div className="bg-white rounded-xl border border-blue-200 p-4 shadow-sm" data-testid="license-pro-count">
                  <p className="text-[10px] text-blue-600 font-semibold uppercase">Pro — 19 EUR/mois</p>
                  <p className="text-2xl font-bold text-blue-700 font-mono">{planBreakdown.pro}</p>
                </div>
                <div className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm" data-testid="license-enterprise-count">
                  <p className="text-[10px] text-amber-600 font-semibold uppercase">Entreprise — 49 EUR/mois</p>
                  <p className="text-2xl font-bold text-amber-700 font-mono">{planBreakdown.enterprise}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100"><h2 className="font-semibold text-sm text-gray-900 flex items-center gap-2"><Crown size={14} className="text-amber-500" /> Licences actives ({filteredLicenses.length})</h2></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-100 text-left">{["Nom", "Email", "Plan", "Depuis", "Source", "Statut", "Actions"].map(h => <th key={h} className="px-5 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                    <tbody>{filteredLicenses.map(l => {
                      const plan = l.plan || "enterprise";
                      return (
                        <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`license-row-${l.id}`}>
                          <td className="px-5 py-3 font-semibold text-gray-900">{l.name}</td>
                          <td className="px-5 py-3 text-xs text-gray-500">{l.email}</td>
                          <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${planColors[plan]?.bg} ${planColors[plan]?.text}`}>{planColors[plan]?.label}</span></td>
                          <td className="px-5 py-3 text-xs text-gray-500">{l.premium_since ? new Date(l.premium_since).toLocaleDateString("fr-FR") : "-"}</td>
                          <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${l.premium_granted_by === "admin" ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"}`}>{l.premium_granted_by === "admin" ? "Admin" : "Stripe"}</span></td>
                          <td className="px-5 py-3">{l.is_validated ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle size={11} /> Actif</span> : <span className="text-xs text-amber-600 flex items-center gap-1"><XCircle size={11} /> Non valide</span>}</td>
                          <td className="px-5 py-3">
                            <div className="flex gap-1.5">
                              {plan === "pro" && <button onClick={() => handleAction(l.id, "grant_premium")} className="bg-amber-50 text-amber-700 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-amber-100">Passer Entreprise</button>}
                              {plan === "enterprise" && <button onClick={() => handleAction(l.id, "grant_pro")} className="bg-blue-50 text-blue-700 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-blue-100">Passer Pro</button>}
                              <button onClick={() => handleAction(l.id, "revoke_premium")} className="bg-rose-50 text-rose-600 px-2 py-1 rounded-lg text-[10px] font-semibold hover:bg-rose-100">Revoquer</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ─── PAYMENTS ─── */}
          {activeSection === "payments" && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-sm text-gray-900 flex items-center gap-2"><CreditCard size={14} className="text-violet-500" /> {payments.length} transaction{payments.length !== 1 ? "s" : ""}</h2>
                <span className="text-xs font-mono font-bold text-emerald-600">{totalRevenue} EUR total</span>
              </div>
              {payments.length === 0 ? <div className="p-8 text-center text-sm text-gray-400">Aucun paiement</div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-100 text-left">{["Email", "Plan", "Montant", "Statut", "Credite", "Date", "Session"].map(h => <th key={h} className="px-5 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                    <tbody>{payments.map((p, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-5 py-3 text-xs font-medium text-gray-700">{p.email}</td>
                        <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.plan === "pro" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>{p.plan === "pro" ? "Pro" : "Entreprise"}</span></td>
                        <td className="px-5 py-3 text-sm font-bold font-mono text-gray-900">{p.amount} EUR</td>
                        <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.payment_status === "paid" ? "Paye" : "En cours"}</span></td>
                        <td className="px-5 py-3">{p.credited ? <CheckCircle size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-gray-300" />}</td>
                        <td className="px-5 py-3 text-xs text-gray-400">{p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}</td>
                        <td className="px-5 py-3 text-[10px] font-mono text-gray-400">{p.session_id?.slice(0, 20)}...</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ─── REVIEWS ─── */}
          {activeSection === "reviews" && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100"><h2 className="font-semibold text-sm text-gray-900 flex items-center gap-2"><Eye size={14} className="text-emerald-500" /> {reviews.length} review{reviews.length !== 1 ? "s" : ""}</h2></div>
              {reviews.length === 0 ? <div className="p-8 text-center text-sm text-gray-400">Aucune review</div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-100 text-left">{["Diagramme", "Commentaires", "Date", "Lien"].map(h => <th key={h} className="px-5 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                    <tbody>{reviews.map(r => (
                      <tr key={r.review_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-5 py-3 font-semibold text-gray-900">{r.diagram_title || "Sans titre"}</td>
                        <td className="px-5 py-3 font-mono text-sm text-gray-700">{r.comment_count}</td>
                        <td className="px-5 py-3 text-xs text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                        <td className="px-5 py-3"><button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/review/${r.review_id}`); toast.success("Lien copié"); }} className="text-indigo-600 hover:text-indigo-800 text-xs flex items-center gap-1"><Copy size={11} /> Copier</button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ─── NOTIFICATIONS ─── */}
          {activeSection === "notifications" && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100"><h2 className="font-semibold text-sm text-gray-900 flex items-center gap-2"><Bell size={14} className="text-indigo-500" /> Notifications système ({notifications.length})</h2></div>
              {notifications.length === 0 ? <div className="p-8 text-center text-sm text-gray-400">Aucune notification</div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-100 text-left">{["Auteur", "Statut", "Message", "Diagramme", "Date", "Lu"].map(h => <th key={h} className="px-5 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                    <tbody>{notifications.map(n => (
                      <tr key={n.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-5 py-3 text-xs font-semibold text-gray-900">{n.author_name}</td>
                        <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${n.status === "approved" ? "bg-emerald-100 text-emerald-700" : n.status === "needs_change" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>{n.status === "approved" ? "Approuvé" : n.status === "needs_change" ? "À modifier" : "Commentaire"}</span></td>
                        <td className="px-5 py-3 text-xs text-gray-500 max-w-xs truncate">{n.text}</td>
                        <td className="px-5 py-3 text-xs text-gray-700">{n.diagram_title}</td>
                        <td className="px-5 py-3 text-xs text-gray-400">{n.created_at ? new Date(n.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                        <td className="px-5 py-3">{n.read ? <span className="text-[10px] text-gray-400">Lu</span> : <span className="w-2 h-2 rounded-full bg-indigo-500 block" />}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ─── TEMPLATES ─── */}
          {activeSection === "templates" && (
            <div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
                <h3 className="font-semibold text-sm text-gray-900 flex items-center gap-2 mb-4"><BookmarkPlus size={14} className="text-indigo-600" /> Créer un nouveau template</h3>
                <div className="flex gap-3 items-end flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Titre</label>
                    <Input value={newTplTitle} onChange={e => setNewTplTitle(e.target.value)} placeholder="Mon template..." className="h-9" data-testid="new-tpl-title" />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Description</label>
                    <Input value={newTplDesc} onChange={e => setNewTplDesc(e.target.value)} placeholder="Description..." className="h-9" />
                  </div>
                  <div className="w-36">
                    <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Categorie</label>
                    <select value={newTplCategory} onChange={e => setNewTplCategory(e.target.value)} className="w-full h-9 rounded-md border border-gray-200 px-2 text-xs focus:outline-none">
                      {["custom", "entreprise", "tech", "workflow", "process", "stratégie", "marketing", "media"].map(c => (
                        <option key={c} value={c}>{c === "custom" ? "Personnalise" : c === "entreprise" ? "Entreprise" : c === "tech" ? "Tech & SaaS" : c === "workflow" ? "Workflow" : c === "process" ? "Production" : c === "stratégie" ? "Stratégie" : c === "marketing" ? "Marketing" : "Creative"}</option>
                      ))}
                    </select>
                  </div>
                  <button onClick={createTemplate} disabled={!newTplTitle.trim()} className="bg-indigo-600 text-white px-4 h-9 rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1 shrink-0" data-testid="create-tpl-btn">
                    <Plus size={12} /> Créer
                  </button>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100"><h2 className="font-semibold text-sm text-gray-900 flex items-center gap-2"><LayoutGrid size={14} className="text-blue-500" /> {templates.length} template{templates.length !== 1 ? "s" : ""}</h2></div>
                {templates.length === 0 ? <div className="p-8 text-center text-sm text-gray-400">Aucun template</div> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-gray-100 text-left">{["Titre", "Description", "Categorie", "Auteur", "Elements", "Date", "Actions"].map(h => <th key={h} className="px-5 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
                      <tbody>{templates.map(t => (
                        <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`template-row-${t.id}`}>
                          <td className="px-5 py-3 font-semibold text-gray-900">{t.title}</td>
                          <td className="px-5 py-3 text-xs text-gray-500 max-w-xs truncate">{t.description || "-"}</td>
                          <td className="px-5 py-3"><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t.category}</span></td>
                          <td className="px-5 py-3 text-xs text-gray-500">{t.author_name || "-"}</td>
                          <td className="px-5 py-3 font-mono text-sm text-gray-700">{t.node_count}</td>
                          <td className="px-5 py-3 text-xs text-gray-400">{t.created_at ? new Date(t.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                          <td className="px-5 py-3">
                            <button onClick={() => deleteTemplate(t.id)} className="bg-rose-50 text-rose-600 px-2.5 py-1 rounded-lg text-[10px] font-semibold hover:bg-rose-100 flex items-center gap-1"><Trash2 size={10} /> Supprimér</button>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── SETTINGS ─── */}
          {activeSection === "settings" && (
            <div className="max-w-2xl space-y-6">
              {/* Plan Limits */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-semibold text-sm text-gray-900 flex items-center gap-2 mb-4"><Settings size={14} className="text-gray-500" /> Limites par plan</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-900">Plan Free — Limite de diagrammes</p>
                      <p className="text-[10px] text-gray-400">Nombre maximum de diagrammes pour les utilisateurs Free</p>
                    </div>
                    <input type="number" value={maxFreeSchemas} onChange={e => setMaxFreeSchemas(Number(e.target.value))} className="w-20 h-9 rounded-lg border border-gray-200 px-3 text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="setting-free-limit" />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-900">Plan Pro — Limite de diagrammes</p>
                      <p className="text-[10px] text-gray-400">Nombre maximum de diagrammes pour les utilisateurs Pro</p>
                    </div>
                    <input type="number" value={maxProSchemas} onChange={e => setMaxProSchemas(Number(e.target.value))} className="w-20 h-9 rounded-lg border border-gray-200 px-3 text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="setting-pro-limit" />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-900">Plan Entreprise — Limite de diagrammes</p>
                      <p className="text-[10px] text-gray-400">Nombre maximum de diagrammes pour les utilisateurs Entreprise</p>
                    </div>
                    <span className="text-sm font-mono text-gray-400">Illimite</span>
                  </div>
                </div>
              </div>

              {/* Stripe Config */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-semibold text-sm text-gray-900 flex items-center gap-2 mb-4"><CreditCard size={14} className="text-violet-500" /> Configuration Stripe</h3>
                <p className="text-[10px] text-gray-400 mb-4">Ajoutez vos cles Stripe pour activer les paiements. Les cles sont stockees de maniere securisee dans la base de donnees.</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Cle publique (pk_...)</label>
                    <Input value={stripePublicKey} onChange={e => setStripePublicKey(e.target.value)} placeholder="pk_live_..." className="font-mono text-xs h-9" data-testid="setting-stripe-pk" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Cle secrete (sk_...)</label>
                    <Input type="password" value={stripeSecretKey} onChange={e => setStripeSecretKey(e.target.value)} placeholder="sk_live_..." className="font-mono text-xs h-9" data-testid="setting-stripe-sk" />
                  </div>
                </div>
              </div>

              {/* SMTP Config */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-semibold text-sm text-gray-900 flex items-center gap-2 mb-4"><Bell size={14} className="text-emerald-500" /> Configuration SMTP (Emails)</h3>
                <p className="text-[10px] text-gray-400 mb-4">Configurez le serveur SMTP pour l'envoi d'emails de bienvenue et de notifications. Compatible PlanetHoster, OVH, Gmail, etc.</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Host SMTP</label>
                      <Input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="mail.mondomaine.com" className="text-xs h-9" data-testid="setting-smtp-host" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Port</label>
                      <Input type="number" value={smtpPort} onChange={e => setSmtpPort(Number(e.target.value))} className="text-xs h-9 font-mono" data-testid="setting-smtp-port" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Email expediteur</label>
                      <Input value={smtpEmail} onChange={e => setSmtpEmail(e.target.value)} placeholder="noreply@mondomaine.com" className="text-xs h-9" data-testid="setting-smtp-email" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Mot de passe SMTP</label>
                      <Input type="password" value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} placeholder="********" className="text-xs h-9" data-testid="setting-smtp-pwd" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 font-semibold uppercase block mb-1">Nom d'expediteur</label>
                    <Input value={smtpFromName} onChange={e => setSmtpFromName(e.target.value)} placeholder="CardFlow" className="text-xs h-9" data-testid="setting-smtp-from" />
                  </div>
                  <button
                    onClick={testSmtp}
                    disabled={testingSmtp || !smtpHost || !smtpEmail}
                    className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1.5 border border-emerald-200"
                    data-testid="test-smtp-btn"
                  >
                    {testingSmtp ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                    {testingSmtp ? "Envoi..." : "Tester l'envoi"}
                  </button>
                </div>
              </div>

              {/* Pricing display */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-semibold text-sm text-gray-900 flex items-center gap-2 mb-4"><Crown size={14} className="text-amber-500" /> Tarification</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                    <p className="text-xs font-bold text-gray-900">Free</p>
                    <p className="text-lg font-bold font-mono text-gray-900 mt-1">0 EUR</p>
                    <p className="text-[10px] text-gray-400 mt-1">{maxFreeSchemas} diagrammes max</p>
                  </div>
                  <div className="p-4 rounded-xl border border-blue-200 bg-blue-50">
                    <p className="text-xs font-bold text-blue-700">Pro</p>
                    <p className="text-lg font-bold font-mono text-blue-700 mt-1">19 EUR / mois</p>
                    <p className="text-[10px] text-blue-500 mt-1">{maxProSchemas} diagrammes max</p>
                  </div>
                  <div className="p-4 rounded-xl border border-amber-200 bg-amber-50">
                    <p className="text-xs font-bold text-amber-700">Entreprise</p>
                    <p className="text-lg font-bold font-mono text-amber-700 mt-1">49 EUR / mois</p>
                    <p className="text-[10px] text-amber-500 mt-1">Diagrammes illimites</p>
                  </div>
                </div>
              </div>

              {/* Save All */}
              <div className="pt-2">
                <button
                  onClick={saveSettings}
                  disabled={settingsSaving}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm"
                  data-testid="save-settings-btn"
                >
                  {settingsSaving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  {settingsSaving ? "Sauvegarde..." : "Sauvegarder tous les reglages"}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
